import { useState, useCallback } from 'react';
import { Row, Col } from 'reactstrap';
import { useDispatch } from 'react-redux';

import Table, { useTable } from 'common/components/table';
import useDrawer from 'common/components/drawer/useDrawer';
import TreeTitleActions from 'common/components/tree-layout/main/TreeTitleActions';
import DangerousActionModal from 'common/components/modals/DangerousActionModal';
import Breadcrumbs from 'common/components/tree-layout/components/Breadcrumbs';
import TextWithTooltip from 'common/components/general/TextWithTooltip';
import NamesInTooltip from '@/library/components/general/NamesInTooltip';

import TableActions from 'components/layouts/table';
import { deletePurchasingGroup } from 'store/purchasing-groups/actions';
import Form from './form';
import EnumsDropdown from '../Dropdown';

const DEPTH_MUTIPLIER = 2;

const PurchasingGroups = () => {
  const [active, setActive] = useState();
  const [type, setType] = useState('create');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, data: null });
  const drawer = useDrawer();
  const dispatch = useDispatch();

  const table = useTable({
    label: 'purchasing_groups',
    columns: [
      {
        header: 'Code',
        key: 'code',
        type: 'string',
        sort: false,
        canFilter: false,
        width: 1
      },
      {
        header: 'Name',
        key: 'name',
        type: 'string',
        sort: false,
        canFilter: false,
        minWidth: 580,
        maxWidth: 580
      },
      {
        header: '',
        key: 'path',
        sort: false,
        minWidth: 440,
        maxWidth: 440,
        canFilter: false
      },
      {
        header: 'Imported to Companies',
        key: 'companies',
        type: 'string',
        sort: false,
        canFilter: false
      }
    ]
  });

  const getActions = useCallback(
    row => [
      {
        name: 'Import to company',
        onClick: () => {
          setActive(row);
          setType('import');
          drawer.open();
        }
      },
      {
        name: 'Edit',
        onClick: () => {
          setActive(row);
          setType('edit');
          drawer.open();
        }
      },
      {
        name: 'Add sub-group',
        onClick: () => {
          setType('subgroup');
          setActive({ parent_id: row?.id });
          drawer.open();
        }
      },
      {
        name: 'Delete',
        onClick: () => {
          setActive(row);

          setDeleteModal({ isOpen: true, data: row });
        }
      }
    ],
    [drawer]
  );

  const onDelete = async id => {
    if (!deleteModal?.data?.id) return;

    try {
      await dispatch(deletePurchasingGroup(deleteModal?.data)).unwrap();
      setDeleteModal({ isOpen: false, data: null });
      table.refetchData();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <Row className="align-items-center mb-4">
        <Col xs={12}>
          <TableActions
            documentTitle="Purchasing Groups"
            title="Enums"
            className="mt-0"
            dropdown={<EnumsDropdown />}
            actions={[
              {
                type: 'create',
                color: 'primary',
                text: 'Add Group',
                className: 'me-3 ms-1 px-2',
                onClick: () => {
                  setType('create');
                  drawer.open();
                }
              }
            ]}
          />
        </Col>
      </Row>

      <Table
        loader
        hideTableFilters
        rows={{
          code: data => (
            <TextWithTooltip className="d-flex align-items-center">
              {data?.code || '-'}
            </TextWithTooltip>
          ),
          name: data => (
            <TextWithTooltip
              className={`d-flex align-items-center ps-${(data?.depth || 0) * DEPTH_MUTIPLIER}`}
            >
              {data?.name || '-'}
            </TextWithTooltip>
          ),
          path: data => {
            const breadcrumbs = data?.hierarchy_path?.map(label => ({
              id: null,
              name: label
            }));

            return (
              <Breadcrumbs
                breadcrumbs={breadcrumbs}
                previewClassName="text-table-color fs-13 fw-normal"
                dropdownMenuClassName="pointer-events-none"
                className="d-flex align-items-center w-100p"
                maxLimit={3}
                onClick
              />
            );
          },
          companies: data => {
            return (
              <div className="d-flex align-items-center">
                <div className="flex-1">
                  <NamesInTooltip
                    maxLimit={5}
                    target="imported-to-companies"
                    names={(data?.linked_organizations || [])?.map(e => e.name)}
                  />
                </div>
                <TreeTitleActions actions={getActions(data) || []} />
              </div>
            );
          }
        }}
        {...table}
      />
      <Form drawer={drawer} active={active} type={type} refetchData={table.refetchData} />

      <DangerousActionModal
        transparent
        action={'Delete Account'}
        onAccept={onDelete}
        closeModal={() => setDeleteModal({ isOpen: false, data: null })}
        isOpen={deleteModal?.isOpen}
        actionText={'DELETE'}
        header={'Delete'}
        body={<div>Are you sure you want to delete this group?</div>}
      />
    </div>
  );
};

export default PurchasingGroups;
